import { z } from "zod";
const key = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/u);
/**
 * How a source read of document lines (bill lines, say) asks the engine for the established pattern of the
 * documents it read: which field names each line's document, the line's amount and tax amount, and the fields
 * that code a line (an account and a tax type, say).
 *
 * A structure is the documents' lines, coded alike, each the same share of the net at the same tax rate. It is
 * established when the newest `minDocuments` (2) documents share it, so a change in how a supplier is coded is
 * followed; otherwise when at least `minDocuments` of the documents read, and at least `minShare` (0.6) of them,
 * share it.
 *
 * Line amounts are net unless `amountType` says otherwise: it names another source read of the same step whose
 * rows name each document (`documentField`) and whether its line amounts include tax (`field`, one of
 * `inclusiveValues`), as a document's header often holds that rather than its lines. `dateField` names each
 * document's date, so the established structure says which period its documents span.
 */
export const workflowDocumentPatternSpecSchema = z.object({
    documentField: key, amountField: key, taxAmountField: key.optional(),
    keyFields: z.array(key).min(1).max(4),
    minDocuments: z.number().int().min(2).max(20).optional(),
    minShare: z.number().min(0.5).max(1).optional(),
    amountType: z.object({ effectKey: key, documentField: key, field: key,
        inclusiveValues: z.array(z.string().min(1).max(100)).min(1).max(8) }).strict().optional(),
    dateField: key.optional(),
}).strict();
/**
 * How a review history read asks for the established pattern of what people signed off: `fieldKey` is the
 * review field whose accepted value holds a document's lines, and the other fields read each line as a source
 * read's do. Each accepted review is one document, dated by its decision.
 */
export const workflowReviewPatternSpecSchema = z.object({
    fieldKey: key, amountField: key, taxAmountField: key.optional(),
    keyFields: z.array(key).min(1).max(4),
    minDocuments: z.number().int().min(2).max(20).optional(),
    minShare: z.number().min(0.5).max(1).optional(),
}).strict();
/** An ISO day from a date, a timestamp or epoch milliseconds; null when the value is none of those. */
const isoDay = (value) => {
    if (typeof value === "number" && Number.isFinite(value))
        return new Date(value).toISOString().slice(0, 10);
    return typeof value === "string" && /^\d{4}-\d{2}-\d{2}/u.test(value) ? value.slice(0, 10) : null;
};
const amount = (value) => {
    const parsed = typeof value === "number" ? value : typeof value === "string" ? Number(value.replaceAll(",", "")) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
};
const round = (value, places) => Math.round(value * 10 ** places) / 10 ** places;
const median = (values) => {
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};
/** The documents whose line amounts include tax, from the rows of the read `amountType` names. */
export function workflowDocumentsWithTaxInclusiveAmounts(rows, amountType) {
    return new Set(rows.filter((row) => amountType.inclusiveValues.includes(String(row[amountType.field] ?? "")))
        .map((row) => String(row[amountType.documentField] ?? "")).filter(Boolean));
}
/**
 * The established pattern of the documents in a source read's rows, which arrive newest document first. A read
 * with more rows than it returned may have cut the oldest document short, so that document is left out. A line
 * of a document in `inclusive` has its tax taken out of its amount, so every line is compared net.
 */
export function workflowDocumentPattern(records, hasMore, spec, inclusive = new Set()) {
    const order = [];
    const grouped = new Map();
    for (const record of records) {
        const id = record[spec.documentField];
        if (typeof id !== "string" && typeof id !== "number")
            continue;
        const document = String(id);
        if (!grouped.has(document)) {
            grouped.set(document, []);
            order.push(document);
        }
        grouped.get(document).push(record);
    }
    if (hasMore)
        order.pop();
    const shapes = [];
    for (const document of order) {
        const lines = grouped.get(document).map((record) => {
            const stated = amount(record[spec.amountField]);
            const tax = spec.taxAmountField ? amount(record[spec.taxAmountField]) ?? 0 : 0;
            return { record, tax, net: stated === null ? null : inclusive.has(document) ? stated - tax : stated };
        });
        const net = lines.reduce((sum, line) => sum + (line.net ?? 0), 0);
        if (lines.some((line) => line.net === null) || net === 0)
            continue;
        const date = spec.dateField ? isoDay(grouped.get(document)[0][spec.dateField]) : null;
        shapes.push({ date, total: net + lines.reduce((sum, line) => sum + line.tax, 0), lines: lines.map(({ record, net: lineNet, tax }) => ({
                keys: Object.fromEntries(spec.keyFields.map((field) => [field, String(record[field] ?? "")])),
                share: lineNet / net, taxRate: lineNet ? tax / lineNet : 0,
            })).sort((a, b) => JSON.stringify(a.keys).localeCompare(JSON.stringify(b.keys)) || b.share - a.share) });
    }
    // Documents share a structure when their lines are coded alike, within a cent in a hundred of net and of rate.
    const signature = (shape) => JSON.stringify(shape.lines.map((line) => [line.keys, round(line.share, 2), round(line.taxRate, 2)]));
    const minDocuments = spec.minDocuments ?? 2;
    // The newest documents agreeing wins, so a supplier whose coding changed is followed as it is coded now.
    let recent = 0;
    while (recent < shapes.length && signature(shapes[recent]) === signature(shapes[0]))
        recent += 1;
    const counts = new Map();
    for (const shape of shapes)
        counts.set(signature(shape), [...counts.get(signature(shape)) ?? [], shape]);
    const [majority] = [...counts.values()].sort((a, b) => b.length - a.length);
    const top = recent >= minDocuments ? shapes.slice(0, recent)
        : majority && majority.length >= minDocuments && majority.length / shapes.length >= (spec.minShare ?? 0.6) ? majority : null;
    if (!top)
        return { documents: shapes.length, established: null };
    const totals = top.map((shape) => shape.total);
    const usual = median(totals);
    const dates = top.map((shape) => shape.date).filter((date) => date !== null).sort();
    // Six places, so a recurring charge sized from its shares comes back to the penny.
    return { documents: shapes.length, established: { documents: top.length,
            lines: top[0].lines.map((line, index) => ({ keys: line.keys,
                share: round(top.reduce((sum, shape) => sum + shape.lines[index].share, 0) / top.length, 6),
                taxRate: round(top.reduce((sum, shape) => sum + shape.lines[index].taxRate, 0) / top.length, 6) })),
            total: round(usual, 2), spread: usual ? round((Math.max(...totals) - Math.min(...totals)) / Math.abs(usual), 4) : 0,
            // Only a pattern that names a date field has a period, so a pattern without one reads as it always has.
            ...(spec.dateField ? { period: dates.length ? { from: dates[0], to: dates.at(-1) } : null } : {}) } };
}
/**
 * The established pattern of what people signed off: each accepted review whose `fieldKey` finding holds lines
 * is one document, dated by its decision. Records arrive newest decision first, as the read returns them.
 */
export function workflowReviewPattern(records, spec) {
    const rows = records.filter((record) => record.decision === "accept").flatMap((record) => {
        const lines = record.findings?.find((finding) => finding.fieldKey === spec.fieldKey)?.proposedValue;
        return Array.isArray(lines) ? lines.filter((line) => !!line && typeof line === "object" && !Array.isArray(line))
            .map((line) => ({ ...line, __review: String(record.evidenceRef), __decided: record.decidedAt })) : [];
    });
    return workflowDocumentPattern(rows, false, { documentField: "__review", dateField: "__decided",
        amountField: spec.amountField, ...(spec.taxAmountField ? { taxAmountField: spec.taxAmountField } : {}),
        keyFields: spec.keyFields, ...(spec.minDocuments ? { minDocuments: spec.minDocuments } : {}),
        ...(spec.minShare ? { minShare: spec.minShare } : {}) });
}
/**
 * A pattern as a reviewer and Twin Judge read it: where it comes from (a person's accepted sign-offs, which count
 * above a source's own coding, or the source's records), how many of the documents read share it, whether they
 * agree (null when too few were read to say), and, for a recurring total, each line's amounts at that total.
 */
export const workflowHistoryEntrySchema = z.object({
    readKey: z.string(),
    basis: z.enum(["signoffs", "source"]),
    sourceLabel: z.string().max(200).nullable(),
    documents: z.number().int().nonnegative(),
    matching: z.number().int().nonnegative(),
    consistent: z.boolean().nullable(),
    period: z.object({ from: z.string(), to: z.string() }).strict().nullable(),
    total: z.number().nullable(),
    lines: z.array(z.object({ keys: z.record(z.string(), z.string()), share: z.number(), taxRate: z.number(),
        net: z.number().nullable(), tax: z.number().nullable() }).strict()).max(50),
    description: z.string().max(2000),
}).strict();
const money = (value) => value.toFixed(2);
const documentsRead = (count) => count === 1 ? "the last document" : `the last ${count} documents`;
/** A pattern as a history entry, with its description in words. */
export function workflowHistoryEntry(readKey, pattern, options) {
    const established = pattern.established;
    const matching = established?.documents ?? 0;
    const consistent = pattern.documents < (options.minDocuments ?? 2) ? null : matching === pattern.documents;
    // A recurring charge is part of the pattern; varying totals are not.
    const total = established && established.spread <= 0.05 ? established.total : null;
    const netTotal = established && total !== null
        ? total / (1 + established.lines.reduce((sum, line) => sum + line.share * line.taxRate, 0)) : null;
    const lines = (established?.lines ?? []).map((line) => {
        const net = netTotal === null ? null : round(line.share * netTotal, 2);
        return { ...line, net, tax: net === null ? null : round(net * line.taxRate, 2) };
    });
    const from = options.basis === "signoffs" ? "Accepted sign-offs, which count above the source's own coding"
        : options.sourceLabel ?? "Source records";
    const coded = (line, index) => `line ${index + 1}: ${Object.entries(line.keys)
        .map(([field, value]) => `${field} ${value || "(blank)"}`).join(", ")}, ${line.net !== null
        ? `${money(line.net)} net, ${line.taxRate ? `${money(line.tax)} tax` : "no tax"}`
        : `${round(line.share * 100, 2)}% of the net, ${line.taxRate ? `tax at ${round(line.taxRate * 100, 2)}%` : "no tax"}`}`;
    const period = established?.period ? ` (${established.period.from} to ${established.period.to})` : "";
    const agreement = consistent === false
        ? ` The other ${pattern.documents - matching} differ, so this history is not consistent.` : "";
    const description = established
        ? `${from}: ${matching} of ${documentsRead(pattern.documents)}${period} share this structure: ${lines.map(coded).join("; ")}.${total !== null ? ` They usually total ${money(total)}.` : ""}${agreement}`
        : pattern.documents === 0 ? `${from}: no earlier documents.`
            : `${from}: no established pattern in ${documentsRead(pattern.documents)}.${consistent === false ? " They are coded differently." : ""}`;
    return { readKey, basis: options.basis, sourceLabel: options.sourceLabel, documents: pattern.documents, matching,
        consistent, period: established?.period ?? null, total, lines, description: description.slice(0, 2000) };
}
